import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { jwtDecode } from 'jwt-decode';
import Cookies from 'js-cookie';

interface JwtPayload {
  sub: string;
  email: string;
  permission?: string | string[]; // Can be single string or array
  exp: number;
  [key: string]: any;
}

export const usePermissionStore = defineStore('permission', () => {
  const permissions = ref<string[]>([]);
  const user = ref<{ id: string; email: string } | null>(null);

  // Load permissions from JWT token
  function loadPermissions() {
    const token = Cookies.get('mat');

    if (!token) {
      permissions.value = [];
      user.value = null;
      return;
    }

    try {
      const decoded = jwtDecode<JwtPayload>(token);

      // Extract permissions from JWT claims
      // The backend stores permissions as multiple "permission" claims
      // jwt-decode will return an array if there are multiple values, or a single string
      const permissionClaim = decoded.permission;
      if (Array.isArray(permissionClaim)) {
        permissions.value = permissionClaim;
      } else if (typeof permissionClaim === 'string') {
        permissions.value = [permissionClaim];
      } else {
        permissions.value = [];
      }

      user.value = {
        id: decoded.sub,
        email: decoded.email
      };
    } catch (error) {
      console.error('Failed to decode JWT token:', error);
      permissions.value = [];
      user.value = null;
    }
  }

  // Check if user has a specific permission
  function hasPermission(permission: string): boolean {
    return permissions.value.includes(permission);
  }

  // Check if user has ANY of the given permissions
  function hasAnyPermission(permissionList: string[]): boolean {
    return permissionList.some(p => permissions.value.includes(p));
  }

  // Check if user has ALL of the given permissions
  function hasAllPermissions(permissionList: string[]): boolean {
    return permissionList.every(p => permissions.value.includes(p));
  }

  // Clear permissions (on logout)
  function clearPermissions() {
    permissions.value = [];
    user.value = null;
  }

  // Computed property to check if user is authenticated
  const isAuthenticated = computed(() => user.value !== null);

  return {
    permissions,
    user,
    isAuthenticated,
    loadPermissions,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    clearPermissions
  };
});
